/**
 * Shared CSS constants for consistent styling across components
 * Based on Meridian design system color tokens
 */
import type { CSSProperties } from 'react';

// ===== TYPOGRAPHY =====
// Font sizes
export const FONT_SIZE_BODY = '14px';        // Regular body text (transcript, notes, fields, questions)
export const FONT_SIZE_SMALL = '13px';       // Labels, timestamps, secondary info
export const FONT_SIZE_XSMALL = '12px';      // Helper text, hints
export const FONT_SIZE_ICON = '16px';              // Status icons (QuestionsToGo emoji)
export const FONT_SIZE_TITLE = '32px';             // Page titles
export const FONT_SIZE_EMPTY_STATE_ICON = '48px';  // Large empty state icons

// Line height
export const LINE_HEIGHT_BODY = '1.6';       // Standard for all body text

// Text colors
export const TEXT_COLOR_PRIMARY = '#000';     // Main body text - always black
export const TEXT_COLOR_SECONDARY = '#999';   // Timestamps, placeholders, empty states, muted info

// Recording/partial text style constants
export const RECORDING_TEXT_COLOR = '#999';
export const RECORDING_TEXT_FONT_STYLE = 'italic';

// Link color
export const TEXT_COLOR_LINK = '#0073bb';          // Author names, clickable links

// ===== COMPOSED TEXT STYLES =====
// Ready-to-use style objects — use with style={TEXT_STYLE_BODY} or spread {...TEXT_STYLE_BODY, marginBottom: '8px'}
export const TEXT_STYLE_BODY: CSSProperties = {
  fontSize: FONT_SIZE_BODY,
  lineHeight: LINE_HEIGHT_BODY,
  color: TEXT_COLOR_PRIMARY,
};

export const TEXT_STYLE_SECONDARY: CSSProperties = {
  fontSize: FONT_SIZE_BODY,
  lineHeight: LINE_HEIGHT_BODY,
  color: TEXT_COLOR_SECONDARY,
};

export const TEXT_STYLE_SMALL: CSSProperties = {
  fontSize: FONT_SIZE_SMALL,
  lineHeight: LINE_HEIGHT_BODY,
  color: TEXT_COLOR_SECONDARY,
};

export const TEXT_STYLE_XSMALL: CSSProperties = {
  fontSize: FONT_SIZE_XSMALL,
  lineHeight: LINE_HEIGHT_BODY,
  color: TEXT_COLOR_SECONDARY,
};

export const TEXT_STYLE_RECORDING: CSSProperties = {
  fontSize: FONT_SIZE_BODY,
  lineHeight: LINE_HEIGHT_BODY,
  color: RECORDING_TEXT_COLOR,
  fontStyle: 'italic',
};

export const TEXT_STYLE_LINK: CSSProperties = {
  fontSize: FONT_SIZE_BODY,
  lineHeight: LINE_HEIGHT_BODY,
  color: TEXT_COLOR_LINK,
  fontWeight: 'bold',
};

// Field status icon colors (used in QuestionsToGo + ExtractedFieldsDisplay)
export const FIELD_ICON_ANSWERED = '#1E8900';      // Green checkmark
export const FIELD_ICON_NOT_SURE = '#F5A623';      // Orange warning
export const FIELD_ICON_UNANSWERED = '#CCD5DE';    // Grey circle

// ===== LAYOUT =====
// Meridian Row backgroundColor presets
export const ROW_BACKGROUND_PRINCIPAL = '#FFFFFF';
export const ROW_BACKGROUND_HIGHLIGHT = '#F5F8FA';
export const ROW_BACKGROUND_SUBDUED = '#F0F2F4';

// Field status background colors
export const FIELD_BG_ANSWERED = '#e8f5e9';     // Green - answered/confirmed
export const FIELD_BG_NOT_SURE = '#fff8e1';     // Yellow - not sure
export const FIELD_BG_UNANSWERED = ROW_BACKGROUND_HIGHLIGHT; // Grey - unanswered
export const FIELD_BG_NOT_APPLICABLE = '#f5f5f5'; // Light grey - N/A

// ===== EVIDENCE HIGHLIGHTING =====
export const EVIDENCE_HIGHLIGHT_BG = '#dbeafe';         // Light blue background for evidence
export const EVIDENCE_HIGHLIGHT_BORDER = '#3b82f6';     // Blue border/accent for evidence
export const EVIDENCE_HIGHLIGHT_TEXT = '#1e40af';        // Dark blue text for evidence badges

// ===== ANIMATIONS =====
export const TRANSITION_BG_COLOR = 'background-color 0.5s ease';
export const HIGHLIGHT_PULSE_DURATION = 1500; // ms
export const HIGHLIGHT_PULSE_COLOR = '#4caf50'; // Green border pulse
